import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Autocomplete, TextField, debounce } from '@mui/material';

import cruiseLineService, { CruiseLine } from '~/services/cruises/CruiseLineService';

interface Props {
  label: string;
  value: string;
  onChange: (cruiseLine: CruiseLine) => void;
  allowEmpty?: boolean;
  required?: boolean;
  disabled?: boolean;
  withApprovedOffers?: boolean;
}

const ITEMS_PER_PAGE = 100;

function InputCruiseLineSelect({ label, value, onChange, allowEmpty, required, disabled, withApprovedOffers }: Props) {
  const [cruiseLines, setCruiseLines] = useState<Array<CruiseLine>>([]);
  const [filteredCruiseLines, setFilteredCruiseLines] = useState<Array<CruiseLine>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialCruiseLine, setInitialCruiseLine] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const getSelectedCruiseLine = useCallback(
    (value: string) => {
      return cruiseLines.find((cruiseLine) => cruiseLine.id === value || cruiseLine.name === value);
    },
    [cruiseLines],
  );

  const fetchCruiseLines = useCallback(
    async (value?: string) => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      if (cruiseLines.length > 0) {
        const isEmptyValue = !value || value.length === 0;
        if (isEmptyValue) {
          setFilteredCruiseLines(cruiseLines);
          setIsLoading(false);
          return;
        }

        const filteredCruiseLines = cruiseLines.filter((cruiseLine) =>
          cruiseLine.name.toLowerCase().includes(value.toLowerCase()),
        );
        if (filteredCruiseLines.length > 0) {
          setFilteredCruiseLines(filteredCruiseLines);
          setIsLoading(false);
          return;
        }
      }

      const res = await cruiseLineService.listWithPagination({
        take: ITEMS_PER_PAGE,
        sortBy: 'name',
        offersApproved: withApprovedOffers,
        ...(value && value.length > 0 ? { name: value } : {}),
      });

      setCruiseLines(res.result);
      setFilteredCruiseLines(res.result);
      setIsLoading(false);
    },
    [cruiseLines, isLoading],
  );

  const handlerOnChange = useCallback(
    (_, value: { value: string; label: string } | null) => {
      if (value === null && allowEmpty) {
        // We need to pass an empty cruise line to avoid errors
        const emptyCruiseLine: CruiseLine = {
          id: '',
          name: '',
          code: '',
          order: 0,
        };
        onChange(emptyCruiseLine);
        return;
      }

      const cruiseLine = getSelectedCruiseLine(value?.value);
      onChange(cruiseLine);
    },
    [allowEmpty, cruiseLines, onChange],
  );

  useEffect(() => {
    fetchCruiseLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialCruiseLine) {
      return;
    }
    const cruiseLine = getSelectedCruiseLine(value);
    if (cruiseLine) {
      onChange(cruiseLine);
      setInitialCruiseLine(true);
    }
  }, [onChange, value, initialCruiseLine, cruiseLines]);

  const cruiseLineSelected = useMemo<{ label: string; value: string } | null>(() => {
    const cruiseLine = getSelectedCruiseLine(value);
    if (!cruiseLine) {
      return null;
    }

    return {
      label: cruiseLine.name,
      value: cruiseLine.id,
    };
  }, [cruiseLines, value]);

  const mappedCruiseLines = useMemo<Array<{ label: string; value: string }>>(() => {
    return filteredCruiseLines.map((cruiseLine) => ({
      label: cruiseLine.name,
      value: cruiseLine.id,
    }));
  }, [filteredCruiseLines]);

  const debouncedFetchCruiseLines = useMemo(() => debounce(fetchCruiseLines, 500), [fetchCruiseLines]);

  return (
    <Autocomplete
      selectOnFocus
      clearOnBlur={false}
      value={cruiseLineSelected}
      loading={isLoading}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={handlerOnChange}
      options={mappedCruiseLines}
      renderInput={(params) => (
        <TextField {...params} required={required} fullWidth label={label} placeholder="Please Enter Cruise Lines" />
      )}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        setInputValue(value);
        debouncedFetchCruiseLines(value);
      }}
      filterOptions={(x) => x}
      disabled={disabled}
    />
  );
}

export default InputCruiseLineSelect;
